/**
 * Platform detection utilities for keyboard shortcuts and platform-specific behavior
 */

/**
 * Detect if the current platform is macOS
 * @returns true if running on macOS, false otherwise
 */
export function isMacPlatform(): boolean {
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
}

/**
 * Get the modifier key state for platform-specific keyboard shortcuts
 * On macOS: checks metaKey (Cmd)
 * On Windows/Linux: checks ctrlKey (Ctrl)
 *
 * @param event - KeyboardEvent to check
 * @returns true if the platform's modifier key is pressed
 */
export function getModifierKey(event: KeyboardEvent): boolean {
  return isMacPlatform() ? event.metaKey : event.ctrlKey;
}

/**
 * Returns true when the element (or document.activeElement) is an editable
 * text context: an <input>, <textarea>, or any contenteditable element.
 *
 * App-level keyboard shortcut handlers that call preventDefault()/stopPropagation()
 * MUST early-return when this returns true — otherwise keystrokes intended for
 * an editor (Plate Cmd-K for link insert, Cmd-B for bold, etc.) are swallowed.
 */
export function isEditableElementFocused(
  el: Element | null = document.activeElement
): boolean {
  if (!el) {
    return false;
  }
  const tag = (el as HTMLElement).tagName;
  if (tag === 'TEXTAREA') {
    return true;
  }
  if (tag === 'INPUT') {
    const input = el as HTMLInputElement;
    return ![
      'button',
      'checkbox',
      'color',
      'file',
      'hidden',
      'image',
      'radio',
      'range',
      'reset',
      'submit',
    ].includes(input.type);
  }
  if ((el as HTMLElement).isContentEditable) {
    return true;
  }
  return false;
}
